// frontend/modules/customer-notification/components/history/NotificationHistoryFilters.tsx
import React from 'react';
import { Form, Input, Select, DatePicker, Button, Card, Row, Col, Space } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { NotificationCategoryType, NotificationPlatforms } from '@/shared/api/model';
import moment from "moment";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface NotificationHistoryFiltersProps {
    searchTerm: string;
    selectedBranch: string | null;
    selectedPlatform: NotificationPlatforms | null;
    selectedCategory: NotificationCategoryType | null;
    startDate: Date | null;
    endDate: Date | null;
    successStatus: boolean | null;
    isLoading: boolean;
    onSearchChange: (value: string) => void;
    onBranchChange: (value: string | null) => void;
    onPlatformChange: (value: NotificationPlatforms | null) => void;
    onCategoryChange: (value: NotificationCategoryType | null) => void;
    onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
    onSuccessStatusChange: (value: boolean | null) => void;
    onResetFilters: () => void;
    onRefresh: () => void;
    branches: { value: string; label: string }[];
}

const NotificationHistoryFilters: React.FC<NotificationHistoryFiltersProps> = ({
    searchTerm,
    selectedBranch,
    selectedPlatform,
    selectedCategory,
    startDate,
    endDate,
    successStatus,
    isLoading,
    onSearchChange,
    onBranchChange,
    onPlatformChange,
    onCategoryChange,
    onDateRangeChange,
    onSuccessStatusChange,
    onResetFilters,
    onRefresh,
    branches
}) => {
    return (
        <Card className="mb-4">
            <Form layout="vertical">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={8} lg={6}>
                        <Form.Item label="Search">
                            <Input
                                placeholder="Search by recipient, template..."
                                value={searchTerm}
                                onChange={(e) => onSearchChange(e.target.value)}
                                prefix={<SearchOutlined />}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item label="Date Range">
                            <RangePicker
                                // @ts-ignore
                                value={startDate && endDate ? [
                                    startDate ? moment(startDate) : null,
                                    endDate ? moment(endDate) : null
                                ] : null}
                                onChange={(dates) => {
                                    if (dates) {
                                        onDateRangeChange(
                                            dates[0] ? dates[0].toDate() : null,
                                            dates[1] ? dates[1].toDate() : null
                                        );
                                    } else {
                                        onDateRangeChange(null, null);
                                    }
                                }}
                                className="w-full"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item label="Branch">
                            <Select
                                placeholder="Select branch"
                                value={selectedBranch}
                                onChange={(value) => onBranchChange(value)}
                                allowClear
                                className="w-full"
                            >
                                {branches.map(branch => (
                                    <Option key={branch.value} value={branch.value}>{branch.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item label="Platform">
                            <Select
                                placeholder="Select platform"
                                value={selectedPlatform}
                                onChange={(value) => onPlatformChange(value)}
                                allowClear
                                className="w-full"
                            >
                                <Option value={NotificationPlatforms.SMS}>SMS</Option>
                                <Option value={NotificationPlatforms.WHATS_APP}>WhatsApp</Option>
                                <Option value={NotificationPlatforms.EMAIL}>Email</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item label="Category">
                            <Select
                                placeholder="Select category"
                                value={selectedCategory}
                                onChange={(value) => onCategoryChange(value)}
                                allowClear
                                className="w-full"
                            >
                                {Object.values(NotificationCategoryType).map((category) => (
                                    <Option key={category} value={category}>
                                        {formatCategoryName(category)}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item label="Status">
                            <Select
                                placeholder="Select status"
                                value={successStatus === null ? undefined : successStatus}
                                onChange={(value) => onSuccessStatusChange(value)}
                                allowClear
                                className="w-full"
                            >
                                <Option value={true}>Delivered</Option>
                                <Option value={false}>Failed</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} sm={24} md={8} lg={6}>
                        <Form.Item label=" " className="mb-0">
                            <Space>
                                <Button 
                                    icon={<FilterOutlined />} 
                                    onClick={onResetFilters}
                                >
                                    Reset Filters
                                </Button>
                                <Button 
                                    type="primary" 
                                    icon={<ReloadOutlined />} 
                                    onClick={onRefresh}
                                    loading={isLoading}
                                >
                                    Refresh
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};

// Helper function to format category names for display
const formatCategoryName = (category: string): string => {
    return category
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export default NotificationHistoryFilters;
